import { Permission } from '@bookora/shared';
import type { RouteObject } from 'react-router-dom';

import { RequirePermission } from '@/app/require-permission';
import { RouteErrorBoundary } from '@/components/route-error-boundary';

import AccountSecurityPage from './account-security-page';
import { TabRedirect } from './tab-redirect';

/**
 * Child routes contributed by the settings feature.
 *
 * The panels themselves are tabs on `/settings?tab=…` rather than routes,
 * because they are one screen: a clinic's details, its rooms and its
 * subscription are read together and switching between them should not remount
 * the section or lose an unsaved form.
 *
 * What IS a route is the set of deep links other features and the API hand out:
 *
 *  · `/settings/onboarding` — the `panelPath` the onboarding checklist returns
 *    for its own step, and the link the dashboard shows a half-configured
 *    clinic.
 *  · `/settings/clinic`, `/settings/schedule`, `/settings/rooms`,
 *    `/settings/verification` — the other `panelPath`s the onboarding checklist
 *    (`backend/src/clinics/services/clinic-onboarding.service.ts`) hands out for
 *    its «انجام این مرحله» buttons. Every one of them MUST resolve to a tab here or
 *    that button dead-ends on «صفحه پیدا نشد». `verification` is its own tab —
 *    the licence upload + review history live in `ClinicVerificationPanel`.
 *  · `/settings/subscription` and `/settings/sms` — where the Zarinpal return
 *    path and the low-credit notification point.
 *
 * Each is a redirect onto the matching tab rather than a second mount of the
 * panel. One implementation, one place a tab's contents can be wrong. Every
 * one goes through `TabRedirect`, which carries the rest of the query along —
 * a `?help=tour:…` deep link included.
 */
export const routes: RouteObject[] = [
  {
    path: 'settings/onboarding',
    element: (
      <RequirePermission anyOf={[Permission.SETTINGS_VIEW]}>
        <TabRedirect tab="onboarding" />
      </RequirePermission>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    // Onboarding «تکمیل مشخصات کلینیک» and «لوگو و معرفی», plus «ارسال مدارک
    // برای تأیید» — the licence panel lives inside the clinic-details tab.
    path: 'settings/clinic',
    element: (
      <RequirePermission anyOf={[Permission.SETTINGS_VIEW]}>
        <TabRedirect tab="clinic" />
      </RequirePermission>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: 'settings/verification',
    element: (
      <RequirePermission anyOf={[Permission.SETTINGS_VIEW]}>
        <TabRedirect tab="verification" />
      </RequirePermission>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    // Onboarding «ساعت کاری پزشکان» — the clinic-wide roster view is the «ساعات
    // کاری» tab.
    path: 'settings/schedule',
    element: (
      <RequirePermission anyOf={[Permission.SETTINGS_VIEW]}>
        <TabRedirect tab="hours" />
      </RequirePermission>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: 'settings/rooms',
    element: (
      <RequirePermission anyOf={[Permission.SETTINGS_VIEW]}>
        <TabRedirect tab="rooms" />
      </RequirePermission>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    // The Zarinpal callback lands here with `?payment=…&ref=…`, and the API
    // strips any query from the return path it is handed (so nobody can craft
    // `?payment=paid`). A bare `<Navigate>` would drop those two parameters and
    // the owner would come back from the gateway to no word about their money.
    path: 'settings/subscription',
    element: (
      <RequirePermission anyOf={[Permission.SETTINGS_BILLING]}>
        <TabRedirect tab="subscription" />
      </RequirePermission>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    // «نتایج و صورت‌حساب» (RaaS) — `BillingPanelPath.RESULTS`, where RaaS
    // notifications and the statement links point. `?focus=` survives.
    path: 'settings/results',
    element: (
      <RequirePermission anyOf={[Permission.SETTINGS_BILLING]}>
        <TabRedirect tab="results" />
      </RequirePermission>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: 'settings/branches',
    element: (
      <RequirePermission anyOf={[Permission.SETTINGS_VIEW]}>
        <TabRedirect tab="branches" />
      </RequirePermission>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: 'settings/integrations',
    element: (
      <RequirePermission anyOf={[Permission.SETTINGS_INTEGRATIONS]}>
        <TabRedirect tab="integrations" />
      </RequirePermission>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: 'settings/sms',
    element: (
      <RequirePermission anyOf={[Permission.SETTINGS_BILLING]}>
        <TabRedirect tab="sms" />
      </RequirePermission>
    ),
    errorElement: <RouteErrorBoundary />,
  },
  {
    /*
     * The ONE route in this file that renders rather than redirects, and the
     * one with no `<RequirePermission>` on it.
     *
     * Two-factor enrolment and session revocation are about the signed-in
     * account, not about the clinic. Redirecting here into `/settings?tab=…`
     * put them behind the section's `settings.view` gate, which منشی and
     * پرستار do not hold — so two of the five role presets could never enrol a
     * second factor or kill a stolen session. See the header of
     * `account-security-page.tsx`.
     */
    path: 'settings/security',
    element: <AccountSecurityPage />,
    errorElement: <RouteErrorBoundary />,
  },
];
